import { defineTable } from "convex/server";
import { v } from "convex/values";
import { internalControl } from "../../../lib/shared/validators";
import { invoiceStatus } from "..";
import { paymentMethods } from "../payments";

export const workOrderInvoiceMode = v.union(
  v.literal("none"),
  v.literal("detailed"),
  v.literal("summary"),
);

export const printTemplates = v.union(
  v.literal("thermal-58"),
  v.literal("thermal-80"),
  v.literal("half-letter"),
  v.literal("letter"),
  v.literal("a4"),
);

// Estilo del encabezado de los PDF (facturas de papel: a4/letter/half-letter).
// - classic:  logo + nombre a la izquierda, contacto a la derecha (el actual).
// - centered: logo centrado, nombre y contacto en columna debajo.
// - minimal:  solo logo + nombre + divisor (sin bloque de contacto).
// - compact:  logo, nombre y contacto en una sola línea.
export const headerStyles = v.union(
  v.literal("classic"),
  v.literal("centered"),
  v.literal("minimal"),
  v.literal("compact"),
);

// Configuración de exención fiscal
export const exemptConfigurationValidator = v.object({
  isEnabled: v.boolean(),
  requiresApproval: v.boolean(),
  approverRoles: v.optional(v.array(v.string())),
  requiredDocuments: v.array(v.id("exempts_documents")),
  rules: v.object({
    allowPartialExemption: v.boolean(),
    requireAllDocuments: v.boolean(), // true = AND, false = OR (al menos uno)
    validateExpiryDates: v.boolean(),
    blockExpiredDocuments: v.boolean(),
    notifyDaysBeforeExpiry: v.optional(v.number()),
  }),
  taxBehavior: v.object({
    applyZeroRate: v.boolean(),
    removeFromInvoice: v.boolean(),
    showOriginalAmount: v.boolean(),
  }),
});

export const creditConfigurationValidator = v.object({
  isEnabled: v.boolean(),
  defaultCreditTermDays: v.number(),
  maxCreditLimitPerClient: v.optional(v.number()),
  requireApprovalAbove: v.optional(v.number()),
  autoBlockOverdue: v.boolean(),
  overdueDaysThreshold: v.number(),
  allowPartialPayments: v.boolean(),
});

export const invoiceConfigurationValidator = v.object({
  branchId: v.id("branches"),
  correlativeId: v.id("correlatives"),

  defaultCurrency: v.string(),
  defaultPaymentMethod: v.optional(paymentMethods),
  defaultPaymentTermDays: v.optional(v.number()),
  allowNegativeStock: v.boolean(),
  allowedCreditPayment: v.boolean(),
  defaultUseCreditEnabled: v.optional(v.boolean()),
  requireClientTaxId: v.boolean(),
  autoGenerateCorrelative: v.boolean(),
  defaultState: v.optional(invoiceStatus),

  requireCorrelative: v.optional(v.boolean()),
  requireClient: v.optional(v.boolean()),
  requireSeller: v.optional(v.boolean()),

  showStatusOnPdf: v.optional(v.boolean()),

  // DEPRECADO: la tasa de impuesto por defecto se unificó en la SUCURSAL
  // (branch.defaultTaxRate). Se deja opcional para no romper documentos
  // existentes; se removerá del schema en una release posterior.
  taxRate: v.optional(v.number()),
  includesTax: v.boolean(),
  // Umbral SAR (Art. 11): monto por encima del cual una venta a Consumidor
  // Final exige consignar los datos de identificación del cliente.
  consumerFinalIdThreshold: v.optional(v.number()),

  exemptConfiguration: v.optional(exemptConfigurationValidator),

  creditConfiguration: v.optional(creditConfigurationValidator),

  workOrderInvoiceMode: v.optional(workOrderInvoiceMode),

  printTemplate: printTemplates,
  printCopies: v.number(),
  printAfterProcess: v.optional(v.boolean()),

  // Personalización del PDF (encabezado y pie de página). Todos opcionales:
  // las configs existentes sin estos campos usan el default `classic`.
  headerStyle: v.optional(headerStyles),
  footerMessage: v.optional(v.string()),
  footerLines: v.optional(v.array(v.string())),
  showSignatureLine: v.optional(v.boolean()),

  // Correlativos de emisión OFFLINE por usuario.
  // Sub-esquema (opcional) embebido: cada entrada asigna a un usuario un
  // `correlatives` DEDICADO con punto de emisión/rango/CAI DISJUNTO del de
  // sucursal y del de otros usuarios (cero colisiones). Prerequisito del
  // offline de facturas.
  userCorrelatives: v.optional(
    v.array(
      v.object({
        userId: v.id("users"),
        correlativeId: v.id("correlatives"),
        enabled: v.boolean(),
        lowBlockThreshold: v.optional(v.number()),
      }),
    ),
  ),
  // Si `true`, la emisión ONLINE de un usuario con entrada `enabled` usa SU
  // correlativo (para entrenar/verificar antes del offline).
  preferUserCorrelativeOnline: v.optional(v.boolean()),

  internalControl,
});

export const invoices_configurations = defineTable(
  invoiceConfigurationValidator,
)
  .index("by_branch_id", ["branchId"])
  .index("by_company_id", ["internalControl.companyId"])
  .index("by_company_and_state", [
    "internalControl.companyId",
    "internalControl.state",
  ])
  .index("by_company_and_branch", [
    "internalControl.companyId",
    "internalControl.branchId",
  ])
  .index("by_branch_and_state", [
    "internalControl.branchId",
    "internalControl.state",
  ])
  .index("by_created_by", ["internalControl.createdBy"])
  .index("by_updated_by", ["internalControl.updatedBy"])
  .index("by_created_at", ["internalControl.createdAt"])
  .index("by_company_and_created_at", [
    "internalControl.companyId",
    "internalControl.createdAt",
  ]);
